import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { paymentHistoryService } from '../../services/paymentHistoryService';
import { format } from 'date-fns';
import { Search, Download, Filter, ChevronDown, ChevronUp, CreditCard, AlertCircle, CheckCircle, Clock, XCircle } from 'lucide-react';
import Icon from '../../components/AppIcon';


export default function PaymentHistory() {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [outstandingInvoices, setOutstandingInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedTransaction, setExpandedTransaction] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    startDate: '',
    endDate: '',
    minAmount: '',
    maxAmount: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    if (user?.id) {
      loadPaymentData();
    }
  }, [user]);

  const loadPaymentData = async () => {
    try {
      setLoading(true);
      setError('');

      const [transactionsData, methodsData, invoicesData] = await Promise.all([
        paymentHistoryService?.getTransactionHistory(user?.id, filters),
        paymentHistoryService?.getPaymentMethods(user?.id),
        paymentHistoryService?.getOutstandingInvoices(user?.id)
      ]);

      setTransactions(transactionsData);
      setPaymentMethods(methodsData);
      setOutstandingInvoices(invoicesData);
    } catch (err) {
      setError(err?.message || 'Failed to load payment data');
      console.error('Payment data load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery?.trim()) {
      loadPaymentData();
      return;
    }

    try {
      setLoading(true);
      const results = await paymentHistoryService?.searchTransactions(user?.id, searchQuery);
      setTransactions(results);
    } catch (err) {
      setError(err?.message || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    loadPaymentData();
    setShowFilters(false);
  };

  const handleResetFilters = () => {
    setFilters({
      status: '',
      startDate: '',
      endDate: '',
      minAmount: '',
      maxAmount: ''
    });
    setShowFilters(false);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      succeeded: { color: 'bg-green-100 text-green-800', icon: CheckCircle, text: 'Completed' },
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, text: 'Pending' },
      failed: { color: 'bg-red-100 text-red-800', icon: XCircle, text: 'Failed' },
      refunded: { color: 'bg-blue-100 text-blue-800', icon: AlertCircle, text: 'Refunded' }
    };

    const config = statusConfig?.[status] || statusConfig?.pending;
    const Icon = config?.icon;

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {config?.text}
      </span>
    );
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    })?.format(amount);
  };

  const toggleTransactionExpand = (transactionId) => {
    setExpandedTransaction(expandedTransaction === transactionId ? null : transactionId);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/4"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
              <p className="text-red-800">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-900">Payment History</h1>
          <button className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            <Download className="w-4 h-4 mr-2" />
            Export All
          </button>
        </div>

        {/* Search and Filter Bar */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by transaction ID, invoice number, or amount..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                onKeyPress={(e) => e?.key === 'Enter' && handleSearch()}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={handleSearch}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Search
            </button>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors inline-flex items-center"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </button>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={filters?.status}
                  onChange={(e) => setFilters({ ...filters, status: e?.target?.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Statuses</option>
                  <option value="succeeded">Completed</option>
                  <option value="pending">Pending</option>
                  <option value="failed">Failed</option>
                  <option value="refunded">Refunded</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={filters?.startDate}
                  onChange={(e) => setFilters({ ...filters, startDate: e?.target?.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={filters?.endDate}
                  onChange={(e) => setFilters({ ...filters, endDate: e?.target?.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Min Amount</label>
                <input
                  type="number"
                  placeholder="0.00"
                  value={filters?.minAmount}
                  onChange={(e) => setFilters({ ...filters, minAmount: e?.target?.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Amount</label>
                <input
                  type="number"
                  placeholder="0.00"
                  value={filters?.maxAmount}
                  onChange={(e) => setFilters({ ...filters, maxAmount: e?.target?.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex items-end gap-2">
                <button
                  onClick={handleApplyFilters}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Apply
                </button>
                <button
                  onClick={handleResetFilters}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Reset
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Outstanding Invoices Section */}
        {outstandingInvoices?.length > 0 && (
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Outstanding Invoices</h2>
            </div>
            <div className="p-6 space-y-4">
              {outstandingInvoices?.map((invoice) => (
                <div key={invoice?.id} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-semibold text-gray-900">{invoice?.orderNumber}</span>
                        {getStatusBadge(invoice?.paymentStatus)}
                      </div>
                      <p className="text-sm text-gray-600">{invoice?.projectName}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Due: {format(new Date(invoice.createdAt), 'MMM dd, yyyy')}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-gray-900">
                        {formatCurrency(invoice?.totalAmount, invoice?.currency)}
                      </p>
                      <button className="mt-2 px-4 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors">
                        Pay Now
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Payment Methods Section */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Payment Methods</h2>
          </div>
          <div className="p-6">
            {paymentMethods?.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No payment methods on file</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {paymentMethods?.map((method) => (
                  <div
                    key={method?.id}
                    className={`border rounded-lg p-4 ${
                      method?.isExpired
                        ? 'border-red-300 bg-red-50'
                        : method?.expiresSoon
                        ? 'border-yellow-300 bg-yellow-50' :'border-gray-200'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <CreditCard className="w-8 h-8 text-gray-600" />
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-gray-900 capitalize">
                              {method?.cardBrand} •••• {method?.cardLast4}
                            </p>
                            {method?.isDefault && (
                              <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded">
                                Default
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">
                            Expires {method?.cardExpMonth}/{method?.cardExpYear}
                          </p>
                          {method?.isExpired && (
                            <p className="text-sm text-red-600 font-medium mt-1">
                              Card Expired - Update Required
                            </p>
                          )}
                          {method?.expiresSoon && !method?.isExpired && (
                            <p className="text-sm text-yellow-600 font-medium mt-1">
                              Expires Soon - Update Recommended
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Transaction History Section */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Transaction History</h2>
          </div>
          <div className="divide-y divide-gray-200">
            {transactions?.length === 0 ? (
              <p className="text-gray-500 text-center py-12">No transactions found</p>
            ) : (
              transactions?.map((transaction) => (
                <div key={transaction?.id} className="hover:bg-gray-50 transition-colors">
                  <div
                    className="px-6 py-4 cursor-pointer"
                    onClick={() => toggleTransactionExpand(transaction?.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-semibold text-gray-900">
                            {transaction?.order?.orderNumber || transaction?.paymentIntentId}
                          </span>
                          {getStatusBadge(transaction?.status)}
                        </div>
                        <p className="text-sm text-gray-600">
                          {transaction?.order?.projectName || 'Payment Transaction'}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {format(new Date(transaction.createdAt), 'MMM dd, yyyy h:mm a')}
                        </p>
                      </div>
                      <div className="flex items-center gap-4">
                        <p className="text-xl font-bold text-gray-900">
                          {formatCurrency(transaction?.amount, transaction?.currency)}
                        </p>
                        {expandedTransaction === transaction?.id ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedTransaction === transaction?.id && (
                    <div className="px-6 pb-4 bg-gray-50 border-t border-gray-200">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-3">Transaction Details</h4>
                          <dl className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <dt className="text-gray-600">Transaction ID:</dt>
                              <dd className="text-gray-900 font-mono text-xs">
                                {transaction?.paymentIntentId}
                              </dd>
                            </div>
                            {transaction?.stripeChargeId && (
                              <div className="flex justify-between">
                                <dt className="text-gray-600">Charge ID:</dt>
                                <dd className="text-gray-900 font-mono text-xs">
                                  {transaction?.stripeChargeId}
                                </dd>
                              </div>
                            )}
                            <div className="flex justify-between">
                              <dt className="text-gray-600">Payment Gateway:</dt>
                              <dd className="text-gray-900 capitalize">{transaction?.gateway}</dd>
                            </div>
                            <div className="flex justify-between">
                              <dt className="text-gray-600">Transaction Type:</dt>
                              <dd className="text-gray-900 capitalize">
                                {transaction?.transactionType}
                              </dd>
                            </div>
                          </dl>
                        </div>

                        {transaction?.order && (
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-3">Invoice Details</h4>
                            <dl className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <dt className="text-gray-600">Invoice Number:</dt>
                                <dd className="text-gray-900">{transaction?.order?.orderNumber}</dd>
                              </div>
                              <div className="flex justify-between">
                                <dt className="text-gray-600">Subtotal:</dt>
                                <dd className="text-gray-900">
                                  {formatCurrency(
                                    transaction?.order?.subtotal,
                                    transaction?.order?.currency
                                  )}
                                </dd>
                              </div>
                              <div className="flex justify-between">
                                <dt className="text-gray-600">Tax:</dt>
                                <dd className="text-gray-900">
                                  {formatCurrency(
                                    transaction?.order?.taxAmount,
                                    transaction?.order?.currency
                                  )}
                                </dd>
                              </div>
                              <div className="flex justify-between border-t pt-2">
                                <dt className="text-gray-900 font-semibold">Total:</dt>
                                <dd className="text-gray-900 font-bold">
                                  {formatCurrency(
                                    transaction?.order?.totalAmount,
                                    transaction?.order?.currency
                                  )}
                                </dd>
                              </div>
                            </dl>
                          </div>
                        )}
                      </div>

                      <div className="mt-4 flex gap-3">
                        <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-white transition-colors inline-flex items-center text-sm">
                          <Download className="w-4 h-4 mr-2" />
                          Download Invoice
                        </button>
                        {transaction?.status === 'failed' && (
                          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm">
                            Retry Payment
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}